import { db, functionsEurope } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  query,
  orderBy,
  limit as limitQuery,
  startAfter,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  getCountFromServer,
  getDoc
} from 'firebase/firestore';
import { validatePlayerName } from '../utils/contentModeration';

const submitLeaderboardScoreCallable = httpsCallable(functionsEurope, 'submitLeaderboardScore');
const resetLeaderboardCallable = httpsCallable(functionsEurope, 'resetLeaderboard');

export const leaderboardService = {
  async getTopScores(gameId, limit = 10, startAfterDoc = null, gameType = null) {
    if (!gameId) return { entries: [], lastVisible: null, hasMore: false };
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
      const dir = 'desc';

      let queryRef = query(
        leaderboardRef,
        orderBy('score', dir),
        limitQuery(limit)
      );

      if (startAfterDoc) {
        queryRef = query(
          leaderboardRef,
          orderBy('score', dir),
          startAfter(startAfterDoc),
          limitQuery(limit)
        );
      }

      const snapshot = await getDocs(queryRef);
      const entries = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(entry => !entry.flagged); // Exclude flagged (suspicious) entries

      return {
        entries,
        lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === limit
      };
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  },

  // Check if user already has an entry (by userId or name+email combo)
  // Used by LeaderboardManager to pre-populate the form
  async findExistingEntry(gameId, userId, name, email) {
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');

      if (userId) {
        const userQuery = query(
          leaderboardRef,
          where('userId', '==', userId),
          limitQuery(1)
        );
        const userSnapshot = await getDocs(userQuery);
        if (!userSnapshot.empty) {
          const doc = userSnapshot.docs[0];
          return { id: doc.id, ...doc.data() };
        }
      }

      // Email-based dedup removed: emails no longer live on entries docs
      // (moved to contactsPrivate for security). Name-based dedup below is
      // sufficient for the public leaderboard form's pre-population.

      if (name) {
        const nameQuery = query(
          leaderboardRef,
          where('name', '==', name.trim()),
          limitQuery(3)
        );
        const nameSnapshot = await getDocs(nameQuery);

        if (!nameSnapshot.empty) {
          const entries = nameSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          return entries[0];
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding existing entry:', error);
      return null;
    }
  },

  async addOrUpdateScore(gameId, scoreData) {
    try {
      if (scoreData.name) {
        const validation = validatePlayerName(scoreData.name);
        if (!validation.isValid) {
          throw new Error(`Invalid name: ${validation.reason}`);
        }
      }

      if (typeof scoreData.score !== 'number' || scoreData.score < 0) {
        throw new Error('Invalid score value');
      }

      if (scoreData.email && scoreData.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(scoreData.email.trim())) {
          throw new Error('Invalid email format');
        }
      }

      const trimmedName = scoreData.name.trim();
      const trimmedEmail = scoreData.email ? scoreData.email.trim() : '';

      // All submissions (new + updates) go through the secure Cloud Function.
      // The function handles duplicate detection, score comparison, and updates server-side.
      const { data } = await submitLeaderboardScoreCallable({
        gameId,
        eventId: scoreData.eventId || null,
        name: trimmedName,
        email: trimmedEmail,
        score: scoreData.score,
        userId: scoreData.userId || null,
        gameType: scoreData.gameType || null,
        metadata: scoreData.metadata || null
      });
      return data;
    } catch (error) {
      console.error('Error adding/updating score:', error);
      throw error;
    }
  },

  // Campaign owner: edit a leaderboard entry (moderation, name/email fixes)
  async updateScore(gameId, recordId, updates) {
    try {
      if (updates.name) {
        const validation = validatePlayerName(updates.name);
        if (!validation.isValid) {
          throw new Error(`Invalid name: ${validation.reason}`);
        }
        updates.name = updates.name.trim();
      }

      if (updates.score !== undefined) {
        if (typeof updates.score !== 'number' || updates.score < 0) {
          throw new Error('Invalid score value');
        }
      }

      // Email + marketingConsent moved to the auth-gated contactsPrivate
      // sibling collection. The entries doc must never carry email again
      // (the public-read rule denies any entries doc that does), and
      // contactsPrivate is server-write-only. Strip these here so admin
      // edits to name/score don't accidentally re-introduce a public email
      // or fail the rule. Email/consent edits need to go through a Cloud
      // Function with Admin SDK access; TODO: wire up updateContactPrivate.
      const { email: _strippedEmail, marketingConsent: _strippedConsent, ...safeUpdates } = updates;

      const docRef = doc(db, 'leaderboard', gameId, 'entries', recordId);
      await updateDoc(docRef, {
        ...safeUpdates,
        timestamp: new Date().toISOString(),
        lastValidated: new Date().toISOString(),
        moderationVersion: '1.0'
      });
      const updatedDoc = await getDoc(docRef);
      return { id: updatedDoc.id, ...updatedDoc.data() };
    } catch (error) {
      console.error('Error updating score:', error);
      throw error;
    }
  },

  async deleteEntry(gameId, entryId) {
    try {
      const docRef = doc(db, 'leaderboard', gameId, 'entries', entryId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting entry:', error);
      throw error;
    }
  },

  /**
   * Reset/delete all entries from a leaderboard via Cloud Function (server-side batched deletes).
   */
  async resetLeaderboard(gameId) {
    const { data } = await resetLeaderboardCallable({ gameId });
    return data;
  },

  /**
   * Pick a single random entry from the full leaderboard without reading
   * the whole collection. Uses a `random` field set at write time and a
   * one-roll query with a wraparound fallback. Costs 1 document read.
   * Returns null if no entries exist (or no entries have the random field).
   */
  async pickRandomEntry(gameId) {
    if (!gameId) return null;
    const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
    const roll = Math.random();
    try {
      const forward = query(
        leaderboardRef,
        where('random', '>=', roll),
        orderBy('random'),
        limitQuery(1)
      );
      let snap = await getDocs(forward);
      if (snap.empty) {
        const backward = query(
          leaderboardRef,
          where('random', '<=', roll),
          orderBy('random', 'desc'),
          limitQuery(1)
        );
        snap = await getDocs(backward);
      }
      if (snap.empty) return null;
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    } catch (error) {
      console.error('Error picking random entry:', error);
      throw error;
    }
  },

  async getTotalEntries(gameId) {
    if (!gameId) return 0;
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');
      const snapshot = await getCountFromServer(leaderboardRef);
      return snapshot.data().count;
    } catch (error) {
      console.error('Error fetching total leaderboard entries:', error);
      throw error;
    }
  },

  /**
   * Fetch contactsPrivate docs for a specific set of entryIds. Used by
   * the admin table view to join the 10 visible entries with their email
   * + consent — bounded to len(ids) reads instead of the whole collection.
   * Returns a Map keyed by entryId.
   */
  async getContactsByIds(gameId, entryIds) {
    if (!gameId || !entryIds?.length) return new Map();
    try {
      const map = new Map();
      await Promise.all(entryIds.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'leaderboard', gameId, 'contactsPrivate', id));
          if (snap.exists()) map.set(id, snap.data());
        } catch {
          // Per-doc failure is non-fatal: the entry just won't have email.
        }
      }));
      return map;
    } catch (error) {
      console.error('Error fetching contactsPrivate by ids:', error);
      return new Map();
    }
  },

  /**
   * Fetch ALL contactsPrivate docs for a campaign. Use ONLY for the CSV
   * export path where the user is explicitly downloading all leads. Reads
   * the entire collection; do not call this in any list/table view.
   */
  async getAllContactsForExport(gameId) {
    if (!gameId) return new Map();
    try {
      const ref = collection(db, 'leaderboard', gameId, 'contactsPrivate');
      const snap = await getDocs(ref);
      const map = new Map();
      snap.forEach(d => { map.set(d.id, d.data() || {}); });
      return map;
    } catch (error) {
      console.error('Error fetching all contactsPrivate for export:', error);
      return new Map();
    }
  },

  async getEmailCount(gameId) {
    if (!gameId) return 0;
    try {
      // Emails moved to the auth-gated contactsPrivate sibling collection;
      // each doc represents one captured lead, so the count is just the
      // collection size. The old where('email','>','') on entries no longer
      // works because the entries doc is forbidden from carrying email at all.
      const contactsRef = collection(db, 'leaderboard', gameId, 'contactsPrivate');
      const snapshot = await getCountFromServer(contactsRef);
      return snapshot.data().count;
    } catch (error) {
      console.error('Error fetching email count:', error);
      throw error;
    }
  },

  async getTopScoresWithUserEntry(gameId, userRecordId, limit = 10, gameType = null) {
    if (!gameId) return { topEntries: [], userEntry: null, userRank: null };
    try {
      const leaderboardRef = collection(db, 'leaderboard', gameId, 'entries');

      const dir = 'desc';
      const topScoresQuery = query(
        leaderboardRef,
        orderBy('score', dir),
        limitQuery(limit)
      );

      const topScoresSnapshot = await getDocs(topScoresQuery);

      const topEntries = topScoresSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data(), isUser: doc.id === userRecordId }))
        .filter(entry => !entry.flagged) // Exclude flagged (suspicious) entries
        .map((entry, index) => ({ ...entry, rank: index + 1 }));

      const userInTopTen = topEntries.some(entry => entry.id === userRecordId);

      return {
        topEntries,
        userEntry: null,
        userRank: userInTopTen
          ? topEntries.find(entry => entry.id === userRecordId)?.rank
          : null
      };
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  },
};
